'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { PageAlertConfig } from '../../types/page-alerts.ts';
type PageAlertOwner = symbol;
type PageAlertRegistration = {
  config: PageAlertConfig;
  owner: PageAlertOwner;
  order: number;
};
type PageAlertActions = {
  registerAlert: (owner: PageAlertOwner, config: PageAlertConfig) => void;
  unregisterAlert: (owner: PageAlertOwner, id: string) => void;
  removeAlert: (id: string) => void;
};
const PageAlertActionsContext = createContext<PageAlertActions | null>(null);
const PageAlertStateContext = createContext<PageAlertConfig[] | null>(null);
const arePageAlertConfigsEqual = (first: PageAlertConfig, second: PageAlertConfig) => {
  const keys = Object.keys(first) as Array<keyof PageAlertConfig>;
  return keys.length === Object.keys(second).length && keys.every(key => Object.is(first[key], second[key]));
};
export const PageAlertProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [registrations, setRegistrations] = useState<Map<string, PageAlertRegistration>>(() => new Map());
  const nextOrder = useRef(0);
  const registerAlert = useCallback((owner: PageAlertOwner, config: PageAlertConfig) => {
    setRegistrations(current => {
      const existing = current.get(config.id);
      if (existing?.owner === owner && arePageAlertConfigsEqual(existing.config, config)) {
        return current;
      }
      const next = new Map(current);
      next.set(config.id, {
        config,
        owner,
        order: existing?.order ?? nextOrder.current++
      });
      return next;
    });
  }, []);
  const unregisterAlert = useCallback((owner_0: PageAlertOwner, id: string) => {
    setRegistrations(current_0 => {
      const existing_0 = current_0.get(id);
      if (!existing_0 || existing_0.owner !== owner_0) {
        return current_0;
      }
      const next_0 = new Map(current_0);
      next_0.delete(id);
      return next_0;
    });
  }, []);
  const removeAlert = useCallback((id_0: string) => {
    setRegistrations(current_1 => {
      if (!current_1.has(id_0)) {
        return current_1;
      }
      const next_1 = new Map(current_1);
      next_1.delete(id_0);
      return next_1;
    });
  }, []);
  const actions = useMemo(() => ({
    registerAlert,
    unregisterAlert,
    removeAlert
  }), [registerAlert, unregisterAlert, removeAlert]);
  const alerts = useMemo(() => [...registrations.values()].sort((a, b) => (b.config.priority ?? 0) - (a.config.priority ?? 0) || a.order - b.order).map(({
    config: config_0
  }) => config_0), [registrations]);
  return <PageAlertActionsContext.Provider value={actions}>
      <PageAlertStateContext.Provider value={alerts}>
        {children}
      </PageAlertStateContext.Provider>
    </PageAlertActionsContext.Provider>;
};
export const usePageAlert = config => {
  const $ = _c(5);
  const actions = useContext(PageAlertActionsContext);
  if (!actions) {
    throw new Error("usePageAlert must be used within <PageAlertProvider />");
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = Symbol("page-alert");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const owner = useRef(t0);
  let t1;
  let t2;
  if ($[1] !== actions || $[2] !== config) {
    t1 = () => {
      if (!config) {
        return;
      }
      actions.registerAlert(owner.current, config);
      return () => actions.unregisterAlert(owner.current, config.id);
    };
    t2 = [actions, config];
    $[1] = actions;
    $[2] = config;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
};
export const usePageAlerts = () => {
  const alerts = useContext(PageAlertStateContext);
  if (!alerts) {
    throw new Error("usePageAlerts must be used within <PageAlertProvider />");
  }
  return alerts;
};
export const useRemovePageAlert = () => {
  const actions = useContext(PageAlertActionsContext);
  if (!actions) {
    throw new Error("useRemovePageAlert must be used within <PageAlertProvider />");
  }
  return actions.removeAlert;
};